import { Col, Form, Row } from "react-bootstrap";
import { useMemo, useState } from "react";

import AppButton from "../../../../commons/buttons/AppButton";
import AppInput from "../../../../commons/inputs/AppInput";
import AppLinkButton from "../../../../commons/buttons/AppLinkButton";
import HallAppToast from "../../../../commons/HallAppToast";
import { useMask } from "@react-input/mask";

const weekOptions = [
  { key: "SUNDAY", label: "Dom" },
  { key: "MONDAY", label: "Seg" },
  { key: "TUESDAY", label: "Ter" },
  { key: "WEDNESDAY", label: "Qua" },
  { key: "THURSDAY", label: "Qui" },
  { key: "FRIDAY", label: "Sex" },
  { key: "SATURDAY", label: "Sáb" },
];

function PromotionStep3({ nextStep, toBack, initialData }) {
  const [highlightAgenda, setHighlightAgenda] = useState(initialData?.highlightAgenda ?? true);
  const [showToClients, setShowToClients] = useState(initialData?.showToClients ?? true);

  const [limitTotalUses, setLimitTotalUses] = useState(initialData?.limitTotalUses ?? "");
  const [limitPerCustomer, setLimitPerCustomer] = useState(initialData?.limitPerCustomer ?? "");

  const [validWeeks, setValidWeeks] = useState(initialData?.validWeeks || []);
  const [startHour, setStartHour] = useState(initialData?.startHour || "");
  const [endHour, setEndHour] = useState(initialData?.endHour || "");

  const startTimeMask = useMask({ mask: "__:__", replacement: { _: /\d/ } });
  const endTimeMask = useMask({ mask: "__:__", replacement: { _: /\d/ } });

  const assignmentMode = initialData?.assignmentMode || "FIXED";

  // ✅ Deriva professionalIds do Step2 (employeeServices: [{serviceId, employeeIds: []}])
  const derivedProfessionalIds = useMemo(() => {
    const es = Array.isArray(initialData?.employeeServices) ? initialData.employeeServices : [];
    const ids = es.flatMap((x) => (Array.isArray(x?.employeeIds) ? x.employeeIds : []));
    return Array.from(new Set(ids.map((n) => Number(n)).filter(Boolean)));
  }, [initialData]);

  const toggleWeek = (key) => {
    setValidWeeks((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  };

  const payload = useMemo(
    () => ({
      highlightAgenda,
      showToClients,
      limitTotalUses,
      limitPerCustomer,
      validWeeks,
      startHour,
      endHour,

      // ✅ mantém consistência com o backend/Step4: professionalIds vem do Step2
      professionalIds: derivedProfessionalIds,
    }),
    [
      highlightAgenda,
      showToClients,
      limitTotalUses,
      limitPerCustomer,
      validWeeks,
      startHour,
      endHour,
      derivedProfessionalIds,
    ]
  );

  const toNext = () => {
    // valida horário se preencheu
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;

    if (startHour && !timeRegex.test(startHour)) {
      HallAppToast.error("Horário inicial inválido (use HH:mm).");
      return;
    }
    if (endHour && !timeRegex.test(endHour)) {
      HallAppToast.error("Horário final inválido (use HH:mm).");
      return;
    }

    // ✅ não valida mais seleção manual de profissionais
    // Só garante que Step2 trouxe algo coerente (opcional)
    if (!derivedProfessionalIds.length) {
      // isso pode acontecer se Step2 ainda não foi preenchido corretamente
      HallAppToast.error("Nenhum profissional foi definido no passo anterior.");
      return;
    }

    nextStep(payload);
  };

  return (
    <div>
      <div className="mb-3">
        <div style={{ fontSize: 16, fontWeight: 700, lineHeight: "20px" }}>Exibição e regras</div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          Defina onde a promoção aparece e, se quiser, limite usos, dias e horários.
        </div>
      </div>

      {/* ✅ INFO: profissionais vêm do Step2 */}
      <div className="card-step mt-3">
        <div className="mb-1" style={{ fontSize: 14, fontWeight: 700 }}>
          Profissionais da promoção
        </div>

        <div className="text-muted" style={{ fontSize: 12 }}>
          {assignmentMode === "FLEXIBLE"
            ? "Modo flexível: o agendamento escolhe automaticamente entre os profissionais permitidos no passo anterior."
            : "Modo fixo: os profissionais foram definidos no passo anterior."}
        </div>

        <div className="mt-2" style={{ fontSize: 12, fontWeight: 800, color: "#1d1b4a" }}>
          {derivedProfessionalIds.length} profissional(is) envolvidos
        </div>
      </div>

      <div className="card-step mt-3">
        <div className="mb-2" style={{ fontSize: 14, fontWeight: 700 }}>
          Limites (opcional)
        </div>

        <Row className="g-2">
          <Col xs={12} sm={6}>
            <AppInput
              label="Limite total de usos"
              name="limitTotalUses"
              value={limitTotalUses}
              onChange={(e) => setLimitTotalUses(e.target.value)}
              placeholder="Ex: 50"
              errors={[]}
              touched={[]}
            />
          </Col>

    
        </Row>
      </div>

      <div className="card-step mt-3">
        <div className="mb-2" style={{ fontSize: 14, fontWeight: 700 }}>
          Dias e horários válidos (opcional)
        </div>

        <div className="d-flex flex-wrap gap-2">
          {weekOptions.map((w) => {
            const selected = validWeeks.includes(w.key);
            return (
              <button
                key={w.key}
                type="button"
                onClick={() => toggleWeek(w.key)}
                style={{
                  borderRadius: 999,
                  border: selected ? "1px solid #2f2a7a" : "1px solid #e8e8f0",
                  background: selected ? "#2f2a7a" : "#fff",
                  color: selected ? "#fff" : "#1d1b4a",
                  padding: "8px 12px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {w.label}
              </button>
            );
          })}
        </div>

        <Row className="g-2 mt-2">
          <Col xs={12} sm={6}>
            <AppInput
              label="Horário inicial (HH:mm)"
              name="startHour"
              value={startHour}
              mask={startTimeMask}
              onChange={(e) => setStartHour(e.target.value)}
              placeholder="Ex: 09:00"
              errors={[]}
              touched={[]}
            />
          </Col>
          <Col xs={12} sm={6}>
            <AppInput
              label="Horário final (HH:mm)"
              name="endHour"
              value={endHour}
              mask={endTimeMask}
              onChange={(e) => setEndHour(e.target.value)}
              placeholder="Ex: 14:00"
              errors={[]}
              touched={[]}
            />
          </Col>
        </Row>
      </div>

      <div className="sticky-footer">
        <AppLinkButton name="Próximo" fullWidth onClick={toNext} />
        <div style={{ marginTop: 10 }}>
          <AppButton name="Voltar" width="100%" onClick={toBack} />
        </div>
      </div>
    </div>
  );
}

export default PromotionStep3;
